import { useRef, useState, useEffect } from "react";
import { PlayIcon, PauseIcon, XIcon } from "lucide-react";
import VastDropdown from "./VastDropdown";
import tv from "../../images/tv.jpg"

export default function CreativeCard({ c, status = "inprogress", progress = 0, bunnyURL, onDelete }) {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingProgress, setProcessingProgress] = useState(0);
    const [processingStatus, setProcessingStatus] = useState("");
    const [isVideoLoading, setIsVideoLoading] = useState(true);
    const [showPreview, setShowPreview] = useState(false);

    // Check if video is processing from backend status
    useEffect(() => {
        if (status === "inprogress" || status === "processing") {
            setIsProcessing(true);
            setProcessingProgress(parseInt(progress));
            setProcessingStatus(status);
        } else if (status === "completed" || status === "ready") {
            setIsProcessing(false);
            setProcessingProgress(100);
        } else if (status === "failed" || status === "error") {
            setIsProcessing(false);
        } else {
            setIsProcessing(false);
        }
    }, [status, progress]);

    // Real-time progress updates from backend
    useEffect(() => {
        if (isProcessing && progress) {
            setProcessingProgress(parseInt(progress));

            // Auto-complete when progress reaches 100
            if (parseInt(progress) >= 100) {
                setTimeout(() => {
                    setIsProcessing(false);
                }, 1000);
            }
        }
    }, [progress, isProcessing]);

    useEffect(() => {
        setIsVideoLoading(true);
    }, [bunnyURL, c?.bunny_url]);

    const handleToggle = () => {
        if (!videoRef.current || isProcessing) return;

        if (isPlaying) {
            videoRef.current.pause();
            setShowControls(true);
        } else {
            videoRef.current.currentTime = 0;
            videoRef.current.play();
            setShowControls(true);
        }

        setIsPlaying(!isPlaying);
    };

    const handleMouseMove = () => {
        if (isPlaying && !isProcessing) setShowControls(true);
    };

    const handleMouseLeave = () => {
        if (isPlaying && !isProcessing) setShowControls(false);
    };

    const openPreview = () => {
        if (!isProcessing && status !== "failed") {
            setShowPreview(true);
        }
    };

    const closePreview = () => {
        setShowPreview(false);
    };

    return (
        <>
            <div className="group relative w-full mx-auto bg-white rounded-2xl border border-slate-200 shadow-xl transition-all duration-300 hover:shadow-2xl hover:border-slate-300 overflow-hidden">
                <div className="relative m-3">
                    <div className="w-full">
                        <img
                            src={tv}
                            className="w-full h-auto block pointer-events-none"
                            alt="TV Frame"
                        />

                        <div
                            className="absolute overflow-hidden bg-black"
                            style={{
                                top: 'clamp(2%, 2.8%, 4%)',
                                left: 'clamp(0.5%, 1.8%, 3.5%)',
                                right: 'clamp(0.5%, 1.8%, 3.5%)',
                                bottom: 'clamp(8%, 11.97%, 14%)',
                            }}
                            onMouseMove={handleMouseMove}
                            onMouseLeave={handleMouseLeave}
                        >
                            {/* Loader */}
                            {(isVideoLoading && !isProcessing) && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/30 z-10">
                                    {/* <div className="h-8 w-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div> */}
                                </div>
                            )}

                            <video
                                ref={videoRef}
                                src={bunnyURL || c?.bunny_url}
                                className="w-full h-full object-cover"
                                muted
                                playsInline
                                autoPlay={!isProcessing}
                                preload="metadata"
                                loop={false}
                                // 🔥 LOADING EVENTS
                                onLoadStart={() => setIsVideoLoading(true)}
                                onWaiting={() => setIsVideoLoading(true)}

                                // ✅ READY EVENTS
                                onLoadedData={() => setIsVideoLoading(false)}
                                onCanPlay={() => setIsVideoLoading(false)}
                                onEnded={() => {
                                    setIsPlaying(false);
                                    setShowControls(true);
                                }}
                                onTimeUpdate={(e) => {
                                    if (e.target.currentTime >= 5 && !isPlaying) e.target.pause();
                                }}
                            />

                            {/* Circular Progress Overlay - Only this remains */}
                            {isProcessing && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-black/80 via-black/70 to-black/80 backdrop-blur-md">
                                    <div className="relative flex flex-col items-center gap-4 min-w-[240px]">
                                        {/* Circular Progress with Percentage */}
                                        <div className="relative">
                                            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 blur-xl opacity-50 animate-pulse"></div>
                                            <div className="relative w-20 h-20">
                                                <svg className="w-20 h-20 transform -rotate-90">
                                                    <circle
                                                        cx="40"
                                                        cy="40"
                                                        r="36"
                                                        fill="none"
                                                        stroke="rgba(255,255,255,0.2)"
                                                        strokeWidth="4"
                                                    />
                                                    <circle
                                                        cx="40"
                                                        cy="40"
                                                        r="36"
                                                        fill="none"
                                                        stroke="url(#gradient)"
                                                        strokeWidth="4"
                                                        strokeLinecap="round"
                                                        strokeDasharray={`${2 * Math.PI * 36}`}
                                                        strokeDashoffset={`${2 * Math.PI * 36 * (1 - processingProgress / 100)}`}
                                                        className="transition-all duration-500 ease-out"
                                                    />
                                                    <defs>
                                                        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                                            <stop offset="0%" stopColor="#fbbf24" />
                                                            <stop offset="100%" stopColor="#f97316" />
                                                        </linearGradient>
                                                    </defs>
                                                </svg>
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <span className="text-white font-bold text-xl">{processingProgress}%</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="text-center space-y-2">
                                            <p className="text-white font-semibold text-base tracking-wide">
                                                {processingStatus === "inprogress" ? "Processing Your Video" : "Preparing Video"}
                                            </p>
                                            <div className="w-full bg-white/20 rounded-full h-1.5">
                                                <div
                                                    className="bg-gradient-to-r from-amber-400 to-orange-500 h-1.5 rounded-full transition-all duration-500"
                                                    style={{ width: `${processingProgress}%` }}
                                                ></div>
                                            </div>

                                            <div className="flex items-center justify-center gap-1 mt-1">
                                                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: '0s' }}></div>
                                                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Failed State Overlay */}
                            {status === "failed" && !isProcessing && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-red-900/80 via-red-800/70 to-red-900/80 backdrop-blur-md">
                                    <div className="relative flex flex-col items-center gap-4 min-w-[240px]">
                                        <div className="relative">
                                            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-red-400 to-rose-500 blur-xl opacity-50 animate-pulse"></div>
                                            <div className="relative w-20 h-20 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                                                <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div className="text-center space-y-2">
                                            <p className="text-white font-semibold text-base tracking-wide">
                                                Processing Failed
                                            </p>
                                            <p className="text-red-300 text-xs font-medium">
                                                Please try again or contact support
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Play/Pause Controls - Only show when not processing */}
                            {!isProcessing && status !== "failed" && (
                                <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${showControls || !isPlaying ? 'opacity-100' : 'opacity-0'
                                    }`}>
                                    <button
                                        onClick={handleToggle}
                                        className="bg-black/40 backdrop-blur-sm text-white rounded-full h-14 w-14 flex items-center justify-center border border-white/30 hover:scale-110 active:scale-95 transition-all hover:bg-black/60"
                                    >
                                        {isPlaying ? (
                                            <PauseIcon className="w-6 h-6" />
                                        ) : (
                                            <PlayIcon className="w-6 h-6 ml-1" />
                                        )}
                                    </button>
                                </div>
                            )}

                            {(!isProcessing && status !== "failed" && !isVideoLoading) && (
                                <button
                                    onClick={openPreview}
                                    className="absolute bottom-[0] right-[0] z-20 bg-black/40 backdrop-blur-sm text-white  p-0.5 transition-all duration-200 hover:bg-black/70 shadow-lg"
                                    aria-label="Fullscreen Preview"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="px-4 py-3 border-t border-slate-200/70">

                    <div className="flex items-center justify-between gap-2 mb-3">
                        {/* <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border
                                 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-100
                                `}>
                            <div className={`w-2 h-2 rounded-full bg-indigo-500`} />
                            <span className={`text-xs font-semibold tracking-wider  text-indigo-600`}>
                                {c?.brand_name}
                            </span>
                        </div> */}
                        <div className="flex items-center gap-1.5 w-max px-2.5 py-1 rounded-lg  bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100">
                            {/* <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 ring-2 ring-indigo-200"></div> */}
                            <span className="text-xs font-semibold text-indigo-600">
                                {c?.brand_name}
                            </span>
                        </div>

                        <div>
                            <div className="flex items-center gap-1.5 ">
                                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span className="text-xs font-medium text-slate-500">
                                    {new Date(c.created_on).toLocaleString("sv-SE")}
                                </span>
                            </div>

                        </div>
                    </div>


                    <div className="mb-4">
                        <h3 title={c?.creative_name} className="text-xl font-bold text-slate-800 leading-tight line-clamp-1">
                            {c?.creative_name}
                        </h3>
                    </div>


                    {/* <div className="flex items-center justify-between border-t border-slate-200/60 pt-2">
                        {status === "completed" && (
                            <div>
                                <VastDropdown c={c} />
                            </div>
                        )}
                    </div> */}


                    <div className="flex items-center gap-3">
                        {status === "completed" && (
                            <div className="flex-1 w-full">
                                <VastDropdown c={c} />
                            </div>
                        )}

                        <button
                            disabled={isProcessing}
                            onClick={() => onDelete?.(c)}
                            className={`flex-1 flex items-center justify-center gap-2 w-full  py-2.5 rounded-xl text-sm font-medium transition-all duration-200
                                            ${isProcessing
                                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                    : "bg-red-50/30 text-red-600 hover:bg-red-100 border border-red-200"
                                }
                                        `}
                        >
                            {isProcessing ? (
                                <>
                                    {status !== "failed" && (<svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>)}
                                    <span>Processing...</span>
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    <span>Delete</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {showPreview && (
                <div className="fixed inset-0 z-[1111] flex items-center justify-center p-3 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="relative w-full max-w-7xl bg-white rounded-2xl overflow-hidden shadow-2xl">
                        {/* Header - Light Theme */}
                        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-white">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow-md">
                                    <PlayIcon className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold">
                                        Video Preview
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        {c?.creative_name}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={closePreview}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-200 text-gray-500 hover:text-gray-700"
                            >
                                <XIcon className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Video Player */}
                        <div className="relative bg-black">
                            <div className="aspect-video">
                                <video
                                    src={bunnyURL || c?.bunny_url}
                                    className="w-full h-full object-contain"
                                    controls
                                    autoPlay
                                    playsInline
                                    preload="auto"
                                />
                            </div>
                        </div>

                        {/* Footer - Light Theme */}
                        <div className="px-4 py-2.5 bg-white border-t border-gray-200">
                            <div className="flex items-center justify-between flex-wrap gap-4">
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
                                        <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
                                        </svg>
                                        <span className="text-sm text-gray-700">{c?.brand_name}</span>
                                    </div>
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
                                        <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span className="text-sm text-gray-700">
                                            {new Date(c.created_on).toLocaleString("sv-SE")}
                                        </span>
                                    </div>
                                </div>

                                {status === "completed" && (
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg">
                                        <div className="text-xs font-medium text-green-600">
                                            VAST Tag Ready
                                        </div>
                                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}